/**
 * Who is signed in, for the whole interface.
 *
 * There is no credential here to hold on to: the login happens on Security
 * and leaves an `HttpOnly` cookie on the API's own origin, which a script on
 * this page cannot read by design (document/40 §1.9 point 6). So "am I signed
 * in?" is not a local question — it is one call to `/auth/sso/me`, and the
 * answer is a person, not a key.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { CurrentProjectAccess } from '@cms/contracts'
import { apiBaseUrl, apiFetch, ApiError, SESSION_EXPIRED_EVENT } from '../api/client.js'
import { projectAccessCapability, type ProjectAccessCapability } from '../domain/capabilities.js'
import { safeReturnPath } from '../routes.js'

export interface Person {
  userId: string
  tenantId: string
  email: string | null
  name: string | null
  picture: string | null
  roles: string[]
  appRoles: string[]
  /** What the roles above grant. Sent by `/auth/sso/me`; see `can`. */
  scopes: string[]
  systemAdmin: boolean
  /** Listed in the gateway's PLATFORM_OPERATORS; absent on an older gateway. */
  platformOperator?: boolean
  projectAccess?: CurrentProjectAccess
  expiresAt: number
}

type State =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'unreachable'; message: string }
  | { status: 'signed-in'; person: Person }

interface SessionContextValue {
  state: State
  projectAccessCapability: ProjectAccessCapability
  /** Leaves the page: the login lives on Security, never here. */
  signIn: (returnTo?: string) => void
  signOut: () => Promise<void>
  reload: () => Promise<void>
}

const SessionContext = createContext<SessionContextValue | null>(null)

/** When the interface last sent an expired session back to the single sign-on. */
const LAST_EXPIRY_REDIRECT_KEY = 'cms:last-expiry-redirect'

/** Where a person who is sent to the login should come back to, across the round trip through Security. */
const PENDING_RETURN_KEY = 'cms:pending-return'

/**
 * The page a person is on, so they come back to it after the login: path and
 * query, which is where the interface keeps every selection. On the login
 * screen itself the answer is the page that sent them there.
 */
export function currentReturnTo(): string {
  const { pathname, search } = window.location
  if (pathname === '/accesso') return safeReturnPath(new URLSearchParams(search).get('ritorno') ?? readPendingReturn())
  return safeReturnPath(`${pathname}${search}`)
}

/**
 * Kept for the one case the URL cannot carry it: the silent check. When it
 * finds nobody signed in, the gateway answers with a bare `/#/login`, and the
 * page the person asked for would be lost without this note.
 */
export function rememberPendingReturn(path: string): void {
  try {
    window.sessionStorage.setItem(PENDING_RETURN_KEY, safeReturnPath(path))
  } catch {
    // Storage disabled: the person lands on the home page instead. Not worth an error.
  }
}

export function readPendingReturn(): string | null {
  try {
    return window.sessionStorage.getItem(PENDING_RETURN_KEY)
  } catch {
    return null
  }
}

export function clearPendingReturn(): void {
  try {
    window.sessionStorage.removeItem(PENDING_RETURN_KEY)
  } catch {
    // Nothing to clear.
  }
}

export function loginUrl(returnTo: string, prompt?: 'none'): string {
  const url = new URL('/auth/sso/login', apiBaseUrl())
  url.searchParams.set('return', returnTo)
  if (prompt) url.searchParams.set('prompt', prompt)
  return url.toString()
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ status: 'loading' })
  // Only a session that existed can expire: an anonymous visit or a logout
  // must land on the login screen, not bounce into a new sign-in.
  const signedIn = useRef(false)
  signedIn.current = state.status === 'signed-in'
  const [projectAccessState, setProjectAccessState] = useState<ProjectAccessCapability>('loading')

  const reloadProjectAccessCapability = useCallback(async () => {
    try {
      const health = await apiFetch<unknown>('/health', { skipAuth: true })
      setProjectAccessState(projectAccessCapability(health))
    } catch {
      setProjectAccessState('unavailable')
    }
  }, [])

  const reload = useCallback(async () => {
    try {
      const person = await apiFetch<Person>('/auth/sso/me')
      setState({ status: 'signed-in', person })
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setState({ status: 'anonymous' })
        return
      }
      setState({
        status: 'unreachable',
        message: error instanceof Error ? error.message : 'Errore sconosciuto.',
      })
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    void reloadProjectAccessCapability()
    const onFocus = () => { void reloadProjectAccessCapability() }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [reloadProjectAccessCapability])

  const signIn = useCallback((returnTo?: string) => {
    const target = safeReturnPath(returnTo ?? currentReturnTo())
    rememberPendingReturn(target)
    window.location.assign(loginUrl(target))
  }, [])

  // Session over while working: straight back through the single sign-on,
  // which usually returns at once to the same page because Security still
  // knows the person. Two bounces within a minute mean that is not working,
  // and the login screen is the honest answer instead of a redirect loop.
  useEffect(() => {
    const onExpired = () => {
      if (!signedIn.current) return
      const now = Date.now()
      let last = 0
      try {
        last = Number(sessionStorage.getItem(LAST_EXPIRY_REDIRECT_KEY) ?? 0)
        sessionStorage.setItem(LAST_EXPIRY_REDIRECT_KEY, String(now))
      } catch {
        // Storage unavailable: fall through to the redirect, still guarded by the state below.
      }
      if (now - last < 60_000) {
        setState({ status: 'anonymous' })
        return
      }
      const target = currentReturnTo()
      rememberPendingReturn(target)
      window.location.assign(loginUrl(target))
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired)
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired)
  }, [])

  const signOut = useCallback(async () => {
    let endSessionUrl: string | null = null
    try {
      const result = await apiFetch<{ endSessionUrl: string | null }>('/auth/sso/logout', {
        method: 'POST',
      })
      endSessionUrl = result.endSessionUrl
    } catch {
      // The session on this side is gone either way; the worst case is that
      // Security still holds its own, which the next login will settle.
    }
    setState({ status: 'anonymous' })
    // Leaving for Security is what makes the logout coordinated rather than
    // local (document/40 §1.7).
    window.location.assign(endSessionUrl ?? '/accesso')
  }, [])

  const value = useMemo<SessionContextValue>(
    () => ({ state, projectAccessCapability: projectAccessState, signIn, signOut, reload }),
    [state, projectAccessState, signIn, signOut, reload],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext)
  if (!context) throw new Error('useSession must be used inside a SessionProvider.')
  return context
}

export function displayName(person: Person): string {
  return person.name ?? person.email ?? person.userId
}

/**
 * Whether the signed-in person holds a scope — the question a screen asks
 * before drawing a button.
 *
 * This hides; it does not protect. The gateway asks the same question on
 * every call and answers 403 on its own (`apps/gateway/src/authz.ts`), which
 * is where the rule actually lives. Here the point is only that a
 * `cms.viewer` should not be offered "Pubblica" and then told no.
 *
 * While the session is still loading the answer is `false`: better a button
 * that appears a moment late than one that appears and is refused.
 */
export function useCan(scope: string): boolean {
  const { state } = useSession()
  return state.status === 'signed-in' && state.person.scopes.includes(scope)
}
